import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, Edit3, Loader2, RefreshCw } from 'lucide-react';
import { CarouselProject } from '@/types';
import { SlideThumbnail } from './SlideThumbnail';
import { useUserStatus } from '@/contexts/UserStatusContext';

type JobState = 'pending' | 'running' | 'done' | 'failed';

interface Props {
  project: CarouselProject;
  imageJobs: Record<string, JobState>;
  selectedSlideIndex: number | null;
  onSelectSlide: (slideIndex: number) => void;
  onRegenerateAll: () => void;
  onExport: () => void;
  isExporting: boolean;
  onBackOnMobile?: () => void;
}

export const ChatCanvas: React.FC<Props> = ({
  project,
  imageJobs,
  selectedSlideIndex,
  onSelectSlide,
  onRegenerateAll,
  onExport,
  isExporting,
  onBackOnMobile,
}) => {
  const navigate = useNavigate();
  const { isPaywalled, setShowPaywall } = useUserStatus();

  const goEdit = () => navigate(`/editor/${project.id}`);

  const handleRegenerateAll = () => {
    if (isPaywalled) {
      setShowPaywall(true);
      return;
    }
    onRegenerateAll();
  };

  return (
    <div
      data-tour="chat-canvas"
      className="h-full flex flex-col bg-background border-l border-border"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3 gap-3">
        {onBackOnMobile && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onBackOnMobile}
            aria-label="Voltar ao chat"
            className="gap-1.5 -ml-2"
          >
            <ArrowLeft className="h-4 w-4" /> Chat
          </Button>
        )}
        <h3 className="text-sm font-semibold truncate flex-1">{project.name}</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRegenerateAll}
          className="gap-1.5"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Regerar tudo
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {project.slides.map((slide, idx) => (
            <SlideThumbnail
              key={slide.id}
              slide={slide}
              index={idx}
              project={project}
              imageJobState={imageJobs[slide.id]}
              selected={selectedSlideIndex === idx}
              onClick={() => onSelectSlide(idx)}
            />
          ))}
        </div>
      </div>

      <div
        data-tour="chat-actions"
        className="border-t border-border px-4 py-3 flex items-center justify-end gap-2"
      >
        <Button variant="outline" size="sm" onClick={goEdit} className="gap-1.5" disabled={isExporting}>
          <Edit3 className="h-3.5 w-3.5" /> Editar completo
        </Button>
        <Button size="sm" onClick={onExport} className="gap-1.5" disabled={isExporting}>
          {isExporting ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Exportando…
            </>
          ) : (
            <>
              <Download className="h-3.5 w-3.5" /> Exportar
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
